import React from 'react';
import type { UnifiedProductSummary } from '@noths/polaris-client-unified-product-service';
import { ProductCarousel } from '@noths/polaris-design-system-candidate-components';

import { SectionContainer } from 'src/components/molecules/SectionContainer/SectionContainer';
import { usePromoteAsSale } from 'src/context/discountPresentationMode/DiscountPresentationModeContext';
import { trackCarouselNavigation } from 'src/tracking/common/carousel';
import { trackProductClick, trackProductImpression } from 'src/tracking/common/products';
import { transformUnifiedProductForTracking } from 'src/tracking/common/unifiedProductTransformer';
import type { TrackingContext } from 'src/types/TrackingContext';
import * as styles from './ProductCarouselSection.style';
import { transformUnifiedProductToProductCarouselItem } from './transformer';

export interface ProductCarouselSectionProps {
  heading: string;
  lazyImages?: boolean;
  pageType: string;
  products: UnifiedProductSummary[];
  trackingPosition?: number;
  trackingTitle: string;
}

export const ProductCarouselSection = ({
  heading,
  lazyImages,
  pageType,
  products,
  trackingPosition,
  trackingTitle,
}: ProductCarouselSectionProps) => {
  const promoteAsSale = usePromoteAsSale();
  const trackingContext: TrackingContext = {
    trackingPosition,
    trackingTitle,
    trackingContentType: 'Product carousel',
  };

  return (
    <SectionContainer columnsXL={10} maxWidth="max" wrapperStyles={styles.wrapper}>
      <ProductCarousel
        heading={heading}
        onNavigate={({ direction, method }) => {
          const trackingDirection = direction === 'next' ? 'next' : 'previous';

          trackCarouselNavigation(method === 'swipe', trackingDirection, {
            ...trackingContext,
            numberOfItems: products.length,
          });
        }}
        /* The payload's own `product` is the OOUX shape the card renders, which does
           not carry what tracking needs (`freeDomesticDelivery`, `salePercentage`,
           the partner shortcode), so the index is used to look the unified product
           back up. */
        onProductClick={(e, { index }) => {
          if (products.length > 0) {
            trackProductClick(e, {
              ...trackingContext,
              pageType,
              product: transformUnifiedProductForTracking(products[index]),
              productIndex: index,
              numberOfItems: products.length,
            });
          }
        }}
        /* One impression event for the whole carousel, listing every product, rather
           than the per-product `onProductVisible`. Keeps the GA payload identical to
           the Ribbons carousel this replaces. */
        onVisible={() => {
          if (products.length > 0) {
            trackProductImpression({
              ...trackingContext,
              pageType,
              products: products.map(transformUnifiedProductForTracking),
            });
          }
        }}
        products={products.map((product) =>
          transformUnifiedProductToProductCarouselItem(product, {
            priority: !lazyImages,
            promoteAsSale,
          }),
        )}
      />
    </SectionContainer>
  );
};
